import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  CreditCard,
  Home,
  BarChart3,
  Package,
  Users,
  Calendar,
  MessageSquare,
  Settings,
  Wrench,
  LogOut,
  X,
  DollarSign,
  Book
} from 'lucide-react';

interface MenuItem {
  path: string;
  label: string;
  icon: string | React.ReactNode;
  requireAuth?: boolean;
  adminOnly?: boolean;
  developerOnly?: boolean;
  isExternal?: boolean;
  externalUrl?: string;
}

interface MenuCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  items: MenuItem[];
  adminOnly?: boolean;
  developerOnly?: boolean;
}

interface AccordionSidebarMenuProps {
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
  isDeveloper?: boolean;
  onLogout: () => void;
}

const AccordionSidebarMenu: React.FC<AccordionSidebarMenuProps> = ({
  isOpen,
  onClose,
  isAdmin,
  isDeveloper = false,
  onLogout
}) => {
  const location = useLocation();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['basic']));

  // カテゴリごとにメニューを整理
  const menuCategories: MenuCategory[] = [
    {
      id: 'basic',
      label: '基本機能',
      icon: <Home className="w-5 h-5" />,
      items: [
        { path: '/', label: 'POSレジ', icon: '💳' },
        { path: '/home', label: 'ホーム画面（ｵﾌﾟｼｮﾝ）', icon: '🏠' },
        { path: '/menu', label: 'メニュー管理', icon: '🍽️', adminOnly: true },
      ]
    },
    {
      id: 'sales',
      label: '売上・分析',
      icon: <BarChart3 className="w-5 h-5" />,
      items: [
        { path: '/sales-management', label: '売上管理', icon: '💰', adminOnly: true },
        { path: '/analytics', label: 'ダッシュボード', icon: '📊', adminOnly: true },
        { path: '/ai-prediction', label: 'AI予測（ｵﾌﾟｼｮﾝ）', icon: '🤖', adminOnly: true }, // 復活: 2026-02-05 実装完了
        // { path: '/line-analytics', label: 'LINE分析', icon: '📊', adminOnly: true }, // 削除: /line/statistics と重複のため非表示
        { path: '/coupon-analytics', label: 'クーポン履歴分析（ｵﾌﾟｼｮﾝ）', icon: '🎫', adminOnly: true },
        { path: '/revenue-analytics', label: 'クーポン収益分析（ｵﾌﾟｼｮﾝ）', icon: '💰', adminOnly: true },
        { path: '/reports/calendar', label: '日報カレンダー', icon: '📅', adminOnly: true },
      ],
      adminOnly: true
    },
    // 在庫カテゴリ - 非表示（2026-02-03）
    // 理由: 在庫管理機能を使用しないため
    // 復活方法: このコメントを外す
    /*
    {
      id: 'inventory',
      label: '在庫',
      icon: <Package className="w-5 h-5" />,
      items: [
        { path: '/stock-input', label: '仕入れ入力', icon: '📥' },
        { path: '/stock-view', label: '在庫確認', icon: '📊' },
      ]
    },
    */
    {
      id: 'accounting',
      label: '経理・会計',
      icon: <DollarSign className="w-5 h-5" />,
      items: [
        { path: '/accounting', label: '会計管理', icon: '📒', adminOnly: true },
        { path: '/expenses', label: '経費管理', icon: '💳', adminOnly: true },
        { path: '/cash-book', label: '現金出納帳', icon: '💵', adminOnly: true },
      ],
      adminOnly: true
    },
    {
      id: 'staff',
      label: 'スタッフ管理',
      icon: <Users className="w-5 h-5" />,
      items: [
        { path: '/attendance', label: 'かんたん勤怠', icon: '⏰' },
        { path: '/attendance-report', label: '勤怠管理', icon: '📋' },
        { path: '/shift-calendar', label: 'シフトカレンダー', icon: '📅' },
        { path: '/payroll', label: '給与管理', icon: '💰', adminOnly: true },
        { path: '/staff', label: 'スタッフ設定', icon: '👥', adminOnly: true },
      ]
    },
    {
      id: 'reservation',
      label: '予約管理',
      icon: <Calendar className="w-5 h-5" />,
      items: [
        { path: '/bento-reservation', label: '弁当予約管理', icon: '🍱' },
        { path: '/bento-inventory', label: '完成品在庫管理', icon: '📦' },
        { path: '/bento-analytics', label: '弁当売上分析', icon: '📊' },
      ]
    },
    {
      id: 'line',
      label: 'LINE連携',
      icon: <MessageSquare className="w-5 h-5" />,
      items: [
        { path: '/line', label: 'LINE会員管理', icon: '💬', adminOnly: true },
        { path: '/point-shop-admin', label: 'ポイントショップ', icon: '🎁', adminOnly: true },
        { path: '/achievements/master', label: '称号マスタ管理（ｵﾌﾟｼｮﾝ）', icon: '👑', adminOnly: true },
        { path: '/achievements/users', label: '会員別称号管理（ｵﾌﾟｼｮﾝ）', icon: '🏅', adminOnly: true },
      ],
      adminOnly: true
    },
    {
      id: 'settings',
      label: 'システム設定',
      icon: <Settings className="w-5 h-5" />,
      items: [
        { path: '/settings', label: '設定', icon: '⚙️' },
        { path: '/admin/feature-flags', label: 'Admin管理', icon: '🔧', developerOnly: true },
        { path: '/admin/permissions', label: '権限項目管理', icon: '🔐', developerOnly: true },
      ]
    },
    {
      id: 'help',
      label: 'ヘルプ',
      icon: <Book className="w-5 h-5" />,
      items: [
        {
          path: '#',
          label: '取扱説明書',
          icon: '📖',
          isExternal: true,
          externalUrl: 'https://line.wa-ton.com/manual/'
        },
      ]
    }
  ];

  // 開発環境の場合、開発者ツールカテゴリを追加
  if (process.env.NODE_ENV === 'development' || window.location.search.includes('dev=true')) {
    menuCategories.push({
      id: 'developer',
      label: '開発者ツール',
      icon: <Wrench className="w-5 h-5" />,
      items: [
        { path: '/developer', label: '開発者ツール', icon: '🔧', adminOnly: true },
      ],
      adminOnly: true
    });
  }

  // カテゴリの展開/折りたたみ
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // アクティブなパスかチェック
  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  // カテゴリ内にアクティブなアイテムがあるかチェック
  const hasActiveItem = (category: MenuCategory) => {
    return category.items.some(item => isActivePath(item.path));
  };

  // 権限に応じてフィルタリング
  const filterItems = (items: MenuItem[]) => {
    return items.filter(item => {
      if (item.developerOnly && !isDeveloper) return false;
      if (item.adminOnly && !isAdmin) return false;
      return true;
    });
  };

  // 権限に応じてカテゴリをフィルタリング
  const filteredCategories = menuCategories.filter(category => {
    if (category.developerOnly && !isDeveloper) return false;
    if (category.adminOnly && !isAdmin) return false;
    // カテゴリ内に表示可能なアイテムがあるかチェック
    const visibleItems = filterItems(category.items);
    return visibleItems.length > 0;
  });

  return (
    <>
      {/* オーバーレイ */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      {/* サイドバー */}
      <div className={`
        fixed top-0 left-0 h-full bg-white shadow-lg z-50 transition-transform duration-300 transform
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        w-72
      `}>
        {/* ヘッダー */}
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">メニュー</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="メニューを閉じる"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* メニューコンテンツ */}
        <div className="overflow-y-auto h-[calc(100vh-140px)]">
          {filteredCategories.map(category => {
            const isExpanded = expandedCategories.has(category.id);
            const isActive = hasActiveItem(category);
            const visibleItems = filterItems(category.items);

            return (
              <div key={category.id} className="border-b">
                {/* カテゴリヘッダー */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  className={`
                    w-full px-4 py-3 flex items-center justify-between
                    hover:bg-gray-50 transition-colors
                    ${isActive ? 'bg-blue-50' : ''}
                  `}
                >
                  <div className="flex items-center gap-3">
                    {category.icon}
                    <span className={`font-medium ${isActive ? 'text-blue-600' : 'text-gray-700'}`}>
                      {category.label}
                    </span>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  )}
                </button>

                {/* カテゴリアイテム */}
                {isExpanded && (
                  <div className="bg-gray-50">
                    {visibleItems.map(item => {
                      // 外部リンクの場合
                      if (item.isExternal && item.externalUrl) {
                        return (
                          <button
                            key={item.path}
                            onClick={() => {
                              window.open(item.externalUrl, '_blank', 'width=1200,height=800');
                              onClose();
                            }}
                            className="
                              w-full text-left px-8 py-2.5 hover:bg-gray-100 transition-colors
                              text-gray-600 hover:text-gray-900
                            "
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-lg">
                                {typeof item.icon === 'string' ? item.icon : item.icon}
                              </span>
                              <span className="text-sm font-medium">{item.label}</span>
                            </div>
                          </button>
                        );
                      }

                      // 通常のルーターリンク
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={onClose}
                          className={`
                            block px-8 py-2.5 hover:bg-gray-100 transition-colors
                            ${isActivePath(item.path)
                              ? 'bg-blue-100 text-blue-700 border-l-4 border-blue-600'
                              : 'text-gray-600 hover:text-gray-900'
                            }
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-lg">
                              {typeof item.icon === 'string' ? item.icon : item.icon}
                            </span>
                            <span className="text-sm font-medium">{item.label}</span>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* フッター */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
          <button
            onClick={onLogout}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            <span>ログアウト</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default AccordionSidebarMenu;
